(function AdzukiDateTimePicker($, Adzuki)
{
	'use strict';

	const component = {
		emits: ['changed', 'update:modelValue'],
		props: {
			clearable: {
				type    : Boolean,
				required: false,
				default : true,
			},
			id: {
				type    : String,
				required: false,
				default : 'date-time-picker',
			},
			includeIcon: {
				type    : Boolean,
				required: false,
				default : true,
			},
			// TS-423: the earliest date that can be picked or typed (YYYY-MM-DD).
			// Empty = no limit, which is every picker that does not pass it.
			minDate: {
				type    : String,
				required: false,
				default : '',
			},
			mode: {
				type    : String,
				required: false,
				default : 'date',
			},
			modelValue: {
				type    : String,
				required: false,
				default : '',
			},
			// TS-423: the month an EMPTY field's calendar opens on (YYYY-MM-DD) --
			// a Return Date opening on the departure month, say. A field holding a
			// date always opens on that date's month.
			openOn: {
				type    : String,
				required: false,
				default : '',
			},
			name: {
				type    : String,
				required: false,
				default : '',
			},
			placeholder: {
				type    : String,
				required: false,
				default : '',
			},
		},
		template: `
			<input
				type="text"
				ref="picker_input"
				class="form-control"
				autocomplete="off"
				@change="inputValueChanged"
				@blur="inputValueEntered"
				:value="formattedVisualValue"
				:id="id"
				:placeholder="placeholder" />
			<input type="hidden" :name="name" :value="formattedValue" />
			<span
				v-if="modelValue && clearable"
				class="fas fa-times position-absolute top-50 translate-middle-y"
				@click="clear"
				:style="{ right: (includeIcon ? '50px' : '10px'), cursor: 'pointer' }">
			</span>
			<span v-if="includeIcon && mode === 'date'" class="input-group-text">
				<i :class="getFaIconStyle('calendar_alt')"></i>
			</span>
			<span v-if="includeIcon && mode === 'time'" class="input-group-text">
				<i :class="getFaIconStyle('clock')"></i>
			</span>
		`,
		computed: {
			formattedVisualValue() {
				if (!this.modelValue) {
					return '';
				}

				return this.format(this.mode === 'date' ? this.dateVisualFormat : this.timeVisualFormat);
			},
			formattedValue() {
				if (!this.modelValue) {
					return '';
				}

				return this.format(this.mode === 'date' ? this.dateFormat : this.timeFormat);
			},
		},
		data() {
			return {
				dateFormat      : 'YYYY-MM-DD',
				dateVisualFormat: 'MM/DD/YYYY',
				picker          : null,
				timeFormat      : 'HH:mm:ss',
				timeVisualFormat: 'h:mm a',
			};
		},
		methods: {
			clear() {
				Adzuki.CustomConfirm({
					body   : 'Are you sure you wish to clear this field?',
					confirm: () => {
						this.$emit('changed', '');
						this.$emit('update:modelValue', '');
					},
				});
			},
			format(format) {
				if (this.mode === 'date') {
					return moment(this.modelValue).format(format);
				}

				const date = moment().format(this.dateFormat);

				return moment(date + ' ' + this.modelValue).format(format);
			},
			getDatePickerOptions() {
				return {
					allowInputToggle: false,
					useCurrent      : false,
					restrictions    : this.getRestrictions(),
					localization    : {
						format             : 'L',
						dayViewHeaderFormat: 'LL',
					},
					display: {
						calendarWeeks: true,
						theme        : 'light',
						viewMode     : 'calendar',
						icons        : {
							type    : 'icons',
							date    : Adzuki.FaIcon.getIconStyle('calendar', 'solid'),
							time    : Adzuki.FaIcon.getIconStyle('clock', 'solid'),
							up      : Adzuki.FaIcon.getIconStyle('chevron_up', 'solid'),
							down    : Adzuki.FaIcon.getIconStyle('chevron_down', 'solid'),
							previous: Adzuki.FaIcon.getIconStyle('chevron_left', 'solid'),
							next    : Adzuki.FaIcon.getIconStyle('chevron_right', 'solid'),
							today   : Adzuki.FaIcon.getIconStyle('dot_circle', 'solid'),
							clear   : Adzuki.FaIcon.getIconStyle('trash', 'solid'),
							close   : Adzuki.FaIcon.getIconStyle('close', 'solid'),
						},
						components: {
							calendar: true,
							date    : true,
							month   : true,
							year    : true,
							clock   : false,
							hours   : false,
							minutes : false,
							seconds : false,
						}
					},
				};
			},
			getRestrictions() {
				if (this.mode !== 'date' || !this.minDate) {
					return {};
				}

				return {
					minDate: new tempusDominus.DateTime(moment(this.minDate, this.dateFormat).startOf('day').toDate()),
				};
			},
			getFaIconStyle(name) {
				return Adzuki.FaIcon.getIconStyle(name);
			},
			getTimePickerOptions() {
				const opts = this.getDatePickerOptions();
				opts.useCurrent                  = true;
				opts.display.viewMode            = 'clock';
				opts.display.components.calendar = false;
				opts.display.components.date     = false;
				opts.display.components.month    = false;
				opts.display.components.year     = false;
				opts.display.components.clock    = true;
				opts.display.components.hours    = true;
				opts.display.components.minutes  = true;
				opts.display.components.seconds  = false;
				opts.localization.format         = 'LT';

				return opts;
			},
			inputValueChanged(e) {
				if (e.target.value === '') {
					this.$emit('changed', '');
					this.$emit('update:modelValue', '');
				}
			},
			inputValueEntered(e) {
				const value = e.target.value;

				if (!value) {
					return;
				}

				const date = moment(value, this.mode === 'date' ? this.dateVisualFormat : this.timeVisualFormat);

				// a typed date before minDate is refused like an unreadable one
				if (!date.isValid() || this.isBeforeMinDate(date)) {
					this.$refs.picker_input.value = '';
					this.setPickerValue('');
					return;
				}

				this.setPickerValue(date.format(this.mode === 'date' ? this.dateFormat : this.timeFormat));
			},
			isBeforeMinDate(date) {
				return this.mode === 'date' && Boolean(this.minDate)
					&& date.isBefore(moment(this.minDate, this.dateFormat), 'day');
			},
			setPickerValue(val) {
				if (!val) {
					this.$emit('changed', '');
					this.$emit('update:modelValue', '');
					return;
				}

				if (this.mode === 'date') {
					const date     = moment(val).toDate();
					const dateTime = new tempusDominus.DateTime(date);
					this.picker.viewDate = dateTime;

					// TODO: this call is causing a few performance issues because the event handler is called, investigate further
					this.picker.dates.setValue(dateTime);
				} else {
					const currentDate  = moment().format(this.dateFormat);
					const dateWithTime = moment(currentDate + ' ' + val).toDate();
					const dateTime     = new tempusDominus.DateTime(dateWithTime);
					this.picker.viewDate = dateTime;
					// TODO: this call is causing a few performance issues because the event handler is called, investigate further
					this.picker.dates.setValue(dateTime);
				}
			}
		},
		watch: {
			modelValue(newVal) {
				this.setPickerValue(newVal);
			},
			minDate() {
				if (this.picker) {
					// the full option set with reset: a merge could not drop an old minDate
					this.picker.updateOptions(this.getDatePickerOptions(), true);
				}
			},
		},
		mounted() {
			let opts = this.getDatePickerOptions();

			if (this.mode === 'time') {
				opts = this.getTimePickerOptions();
			}

			this.picker = new tempusDominus.TempusDominus(this.$refs.picker_input, opts);

			// initial date/time is set
			if (this.modelValue) {
				this.setPickerValue(this.modelValue);
			}

			// TS-423: an empty field opens on openOn's month rather than today's
			this.picker.subscribe(tempusDominus.Namespace.events.show, () => {
				if (!this.modelValue && this.openOn && this.mode === 'date') {
					this.picker.viewDate = new tempusDominus.DateTime(moment(this.openOn, this.dateFormat).toDate());
				}
			});

			this.picker.subscribe(tempusDominus.Namespace.events.change, (e) => {
				let newDate = '';

				if (e.date) {
					newDate = moment(e.date).format(this.mode === 'date' ? this.dateFormat : this.timeFormat);
				}

				this.$emit('changed', newDate);
				this.$emit('update:modelValue', newDate);
			});
		},
	};

	Adzuki.VueComponents.AdzukiDateTimePicker = component;
}(jQuery, window.Adzuki || {}));
